import React, { FC, useEffect } from 'react'

import { colors, TrustPilot } from '@smarty-nx/ds'
import {
  DATA_LAYER_SCREEN_TYPES,
  ORDER_TYPES,
  triggerScreenLoadEvent,
} from '@smarty-nx/tracking'
import { graphql, HeadProps } from 'gatsby'

import CompareGroupPlansCalculator from './compare-group-plans-calculator'
import GroupPlanBenefits from './group-plan-benefits'
import classes from './group-plans.module.scss'
import PageHead from '../../components/PageHead'
import { FullWidthContainer, SiteContainer } from '../../components/shared'
import spacerClasses from '../../components/styles/spacer.module.scss'
import withApp, { AppProps } from '../../components/withApp/withApp'
import { FEATURES } from '../../config'
import {
  createFAQPageSchema,
  mapFaqItemsToSchemaItems,
} from '../../constants/structuredData'
import FaqSection from '../../modular-system/FaqSection'
import { FaqSectionProps } from '../../modular-system/FaqSection/FaqSection'
import { HeroBanner } from '../page-template/page-sections'
import { pageSectionsMapping } from '../page-template/page-sections-mapping'

interface GroupPlansProps {
  data: {
    contentfulPageSection: Queries.GenericPageSectionFragment
    whyChooseGroup: Queries.GenericPageSectionFragment
    groupPlanFAQ: FaqSectionProps
    location: { pathname: string }
  }
}

const GroupPlans: FC<AppProps & GroupPlansProps> = ({
  fetchGroupPlans,
  data: { contentfulPageSection, whyChooseGroup, groupPlanFAQ },
}) => {
  useEffect(() => {
    fetchGroupPlans({ alwaysWithGroupDiscount: true })

    triggerScreenLoadEvent({
      name: 'unlimited-family-plan',
      type: DATA_LAYER_SCREEN_TYPES.PRODUCT_DETAILS,
      order_type: ORDER_TYPES.GROUP_PLAN_SOLO_USER_CHECKOUT,
    })
  }, [fetchGroupPlans])

  const type =
    whyChooseGroup.displayContentAs as keyof typeof pageSectionsMapping

  const Component = pageSectionsMapping[type]
  return (
    <div className={classes.groupPlans}>
      <FullWidthContainer background={colors[whyChooseGroup.bg?.theme || '']}>
        <SiteContainer>
          <div className={classes.mainHeader}>
            <HeroBanner index={0} {...contentfulPageSection} />
          </div>
          {Component && (
            <Component index={1} key={whyChooseGroup.id} {...whyChooseGroup} />
          )}
          <CompareGroupPlansCalculator />
          <GroupPlanBenefits />
          <TrustPilot
            dataTestId="homepage-trustpilot-widget"
            reviewTag={
              FEATURES.TRUSTPILOT_UPDATE
                ? 'SMARTY_Homepage_Reviews'
                : 'websitewidget'
            }
            featureTrustPilot={FEATURES.TRUSTPILOT_UPDATE}
          />
        </SiteContainer>
        <SiteContainer
          className={`${classes.faq} ${spacerClasses.spacerBottom}`}
        >
          <FaqSection {...groupPlanFAQ} />
        </SiteContainer>
      </FullWidthContainer>
    </div>
  )
}

export default withApp(GroupPlans)

export const Head: FC<HeadProps<GroupPlansProps['data']>> = ({
  data,
  location,
}) => {
  const faqSchema = createFAQPageSchema(
    mapFaqItemsToSchemaItems(data.groupPlanFAQ?.items || [])
  )

  return (
    <PageHead
      title="Save on every SIM with a Group plan"
      description="Enjoy discounted plans. Save on every SIM you add to your Group. Stay flexible if you change your mind. Save time and money. Thats's SMARTY"
      location={location}
    >
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </PageHead>
  )
}

export const query = graphql`
  query {
    contentfulPageSection(sectionName: { eq: "group-landing-page-header" }) {
      ...LandingHeaderSection
    }
    whyChooseGroup: contentfulPageSection(
      sectionName: { eq: "why-choose-a-smarty-group" }
    ) {
      ...GenericPageSection
    }
    groupPlanFAQ: contentfulPageSection(
      sectionName: { eq: "group-plan-faq-list" }
    ) {
      ...FaqSection
    }
  }
`
